import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { teamInvitations, teamMembers } from "../services/supabase";
import "./account.css";

type Mode = "signin" | "signup" | "forgot" | "reset";

const GENERIC_RESET_MESSAGE = "If an account exists for that email, we've sent a password reset link. Check your inbox (and spam folder).";

const Account: React.FC = () => {
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [username, setUsernameInput] = useState<string>("");
  const [fullName, setFullName] = useState<string>("");
  const [gender, setGender] = useState<string>("");
  const [tshirtSize, setTshirtSize] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [invitation, setInvitation] = useState<any>(null);
  const [processingInvite, setProcessingInvite] = useState<boolean>(false);

  const { user, login, signup, logout, isPasswordRecovery, requestPasswordReset, updatePassword } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');

  useEffect(() => {
    if (inviteToken) {
      setMode("signup");
      loadInvitation(inviteToken);
    }
  }, [inviteToken]);

  useEffect(() => {
    if (isPasswordRecovery) {
      setMode("reset");
      setError("");
      setMessage("");
    }
  }, [isPasswordRecovery]);

  useEffect(() => {
    if (user && inviteToken && !processingInvite) {
      acceptInvitation();
    }
  }, [user, inviteToken]);

  const loadInvitation = async (token: string) => {
    try {
      const { data, error } = await teamInvitations.getByToken(token);
      if (error) throw error;

      if (!data) { setError("Invitation not found"); return; }
      if (data.status !== 'pending') { setError("This invitation has already been used"); return; }

      const expiresAt = new Date(data.expires_at);
      if (expiresAt < new Date()) { setError("This invitation has expired"); return; }

      setInvitation(data);
      setMessage(`You've been invited to join ${data.teams?.team_name || 'a team'}! ${mode === 'signup' ? 'Create your account below.' : 'Sign in to accept.'}`);
    } catch (err) {
      console.error("Failed to load invitation:", err);
      setError("Failed to load invitation details");
    }
  };

  const acceptInvitation = async () => {
    if (!user || !inviteToken || !invitation) return;
    setProcessingInvite(true);
    try {
      const { error: memberError } = await teamMembers.create({
        team_id: invitation.team_id,
        user_id: user.id,
        role: 'member'
      });

      if (memberError && memberError.code !== '23505') throw memberError;

      await teamInvitations.accept(inviteToken);
      setMessage(`Successfully joined ${invitation.teams?.team_name || 'the team'}!`);
      setTimeout(() => navigate(`/team/${invitation.team_id}`), 1500);
    } catch (err: any) {
      console.error("Failed to accept invitation:", err);
      setError("Failed to join team. Please try again.");
    } finally {
      setProcessingInvite(false);
    }
  };

  const handleSubmit = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);
    try {
      if (mode === "signin") {
        await login(email, password);
        setMessage("Signed in successfully!");
        if (!inviteToken) setTimeout(() => navigate("/"), 1000);
      } else if (mode === "forgot") {
        await requestPasswordReset(email.trim());
        setMessage(GENERIC_RESET_MESSAGE);
        setEmail("");
      } else if (mode === "reset") {
        if (password.length < 8) throw new Error("Password must be at least 8 characters.");
        if (password !== confirmPassword) throw new Error("Passwords do not match.");

        await updatePassword(password);
        await logout();
        setPassword("");
        setConfirmPassword("");
        setMessage("Password updated! Please sign in with your new password.");
        setMode("signin");
      } else {
        if (!username.trim()) throw new Error("Username is required");
        if (!fullName.trim()) throw new Error("Full name is required");
        if (!gender) throw new Error("Please select your gender");
        if (!tshirtSize) throw new Error("Please select your t-shirt size");

        const { alreadyRegistered, alreadyConfirmed } = await signup(email, password, {
          username: username.trim(),
          full_name: fullName.trim(),
          gender,
          tshirt_size: tshirtSize
        });
        if (alreadyConfirmed) {
          setMessage("An account with this email already exists. Please sign in instead.");
        } else if (alreadyRegistered) {
          setMessage("An account with this email already exists but isn't confirmed yet — we've sent a new confirmation email. Check your inbox.");
        } else {
          setMessage("Account created! Check your email for confirmation.");
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleModeSwitch = (newMode: Mode) => {
    setMode(newMode);
    setError("");
    setMessage("");
    setPassword("");
    setConfirmPassword("");
    if (invitation && (newMode === 'signin' || newMode === 'signup')) {
      setMessage(`You've been invited to join ${invitation.teams?.team_name || 'a team'}! ${newMode === 'signup' ? 'Create your account below.' : 'Sign in to accept.'}`);
    }
  };

  if (processingInvite) {
    return (
      <div className="account-page">
        <div className="ac-processing">
          <div className="ac-processing-title">Joining Team...</div>
          <p className="ac-processing-desc">
            Please wait while we add you to {invitation?.teams?.team_name || 'the team'}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="account-page">

      {(mode === 'signin' || mode === 'signup') && (
        <div className="ac-tabs">
          <button
            className={`ac-tab ${mode === 'signin' ? 'active' : ''}`}
            onClick={() => handleModeSwitch('signin')}
          >
            Sign In
          </button>
          <button
            className={`ac-tab ${mode === 'signup' ? 'active' : ''}`}
            onClick={() => handleModeSwitch('signup')}
          >
            Create Account
          </button>
        </div>
      )}

      <h1 className="ac-title">
        {mode === 'forgot' ? 'Reset Password' : mode === 'reset' ? 'Set New Password' : mode === 'signup' ? 'Create Account' : 'Sign In'}
      </h1>

      {inviteToken && invitation && (
        <div className="ac-invite">
          <div className="ac-invite-label">Team Invitation</div>
          <p className="ac-invite-text">
            You've been invited to join <strong>{invitation.teams?.team_name || 'a team'}</strong>.{' '}
            {mode === 'signup' ? 'Create your account below.' : 'Sign in to accept.'}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
      {mode === 'signup' && (
        <>
          <div className="ac-field">
            <label className="ac-label">Full Name</label>
            <input
              type="text"
              className="ac-input"
              placeholder="Joe Bloggs"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="ac-field">
            <label className="ac-label">Username</label>
            <input
              type="text"
              className="ac-input"
              placeholder="tom_layhe"
              value={username}
              onChange={(e) => setUsernameInput(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="ac-field">
            <label className="ac-label">Gender</label>
            <div className="ac-select-wrap">
              <select
                className="ac-select"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                disabled={loading}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non-binary">Non-binary</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </select>
            </div>
          </div>

          <div className="ac-field">
            <label className="ac-label">T-Shirt Size</label>
            <div className="ac-select-wrap">
              <select
                className="ac-select"
                value={tshirtSize}
                onChange={(e) => setTshirtSize(e.target.value)}
                disabled={loading}
              >
                <option value="">Select Size</option>
                <option value="XS">XS</option>
                <option value="S">S</option>
                <option value="M">M</option>
                <option value="L">L</option>
                <option value="XL">XL</option>
                <option value="XXL">XXL</option>
              </select>
            </div>
          </div>
        </>
      )}

      {(mode === 'signin' || mode === 'signup') && (
        <>
          <div className="ac-field">
            <label className="ac-label">Email</label>
            <input
              type="email"
              className="ac-input"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="ac-field">
            <label className="ac-label">Password</label>
            <input
              type="password"
              className="ac-input"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
          </div>

          {mode === 'signin' && (
            <div className="ac-forgot">
              <button
                type="button"
                className="ac-switch-btn"
                onClick={() => handleModeSwitch('forgot')}
              >
                Forgot your password?
              </button>
            </div>
          )}
        </>
      )}

      {mode === 'forgot' && (
        <div className="ac-field">
          <label className="ac-label">Email</label>
          <input
            type="email"
            className="ac-input"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
        </div>
      )}

      {mode === 'reset' && (
        <>
          <div className="ac-field">
            <label className="ac-label">New Password</label>
            <input
              type="password"
              className="ac-input"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="ac-field">
            <label className="ac-label">Confirm New Password</label>
            <input
              type="password"
              className="ac-input"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
            />
          </div>
        </>
      )}

      {error && <div className="ac-error">{error}</div>}
      {message && <div className="ac-success">{message}</div>}

      <button
        type="submit"
        className="ac-submit"
        disabled={loading}
      >
        {loading
          ? 'Processing...'
          : mode === 'signup'
          ? 'Create Account'
          : mode === 'forgot'
          ? 'Send Reset Link'
          : mode === 'reset'
          ? 'Set New Password'
          : 'Sign In'}
      </button>
      </form>

      {mode === 'signup' && (
        <div className="ac-switch">
          Already have an account?
          <button className="ac-switch-btn" onClick={() => handleModeSwitch('signin')}>
            Sign In
          </button>
        </div>
      )}

      {mode === 'signin' && (
        <div className="ac-switch">
          New here?
          <button className="ac-switch-btn" onClick={() => handleModeSwitch('signup')}>
            Create Account
          </button>
        </div>
      )}

      {mode === 'forgot' && (
        <div className="ac-switch">
          Remembered your password?
          <button className="ac-switch-btn" onClick={() => handleModeSwitch('signin')}>
            Sign In
          </button>
        </div>
      )}

    </div>
  );
};

export default Account;
